<template>
  <!-- Top progress bar shown while routes are resolving (covers async setup
       and data fetches in page components). Color follows the active tenant
       theme via the --primary CSS variable. -->
  <NuxtLoadingIndicator
    color="var(--primary, rgb(0, 242, 255))"
    :height="2"
    :throttle="120"
    :duration="2500"
  />

  <NuxtLayout>
    <NuxtPage />
  </NuxtLayout>
  <!-- ChatPanel is mounted here (not inside layouts) so the socket survives
       route changes to pages with `layout: false` (e.g. the workflow editor).
       Moving it into a layout caused the panel to unmount/remount between
       routes, dropping the WS connection on every navigation.
       We defer rendering until after `onMounted` because `showChat`
       reads session state that can diverge between SSR (cookie snapshot)
       and CSR (session re-sync). Rendering only post-mount sidesteps
       hydration mismatches. -->
  <ChatPanel v-if="chatMounted && showChat" />
  <CookieConsentCookieBanner />
  <!-- Maintenance gate. Both components self-gate from the tenant
       state + session — non-admins on a maintenance-locked tenant see
       the full-screen overlay; admins see a small persistent banner
       reminding them visitors are locked out. Rendered here (above
       NuxtLayout) so the overlay covers admin/creator/shop chrome
       uniformly and a stale layout can't leak through. -->
  <MaintenanceOverlay />
  <MaintenanceAdminBanner />
</template>

<script setup>
const { appName, tenant } = useTenantTheme()
const { loggedIn, user } = useUserSession()

// Flipped to true in onMounted — keeps showChat-dependent DOM out of the
// SSR output so the initial hydration matches. The chat panel mounts one
// tick after hydration, which is invisible to users.
const chatMounted = ref(false)
onMounted(() => { chatMounted.value = true })

// Chat is a back-office feature — only surface it inside the admin, creator
// and superadmin panels (identified by subdomain). Public shop / artist /
// landing pages should stay free of the floating chat button.
const route = useRoute()
const host = computed(() => {
  if (import.meta.server) return (useRequestURL().hostname || '').toLowerCase()
  return (window.location.hostname || '').toLowerCase()
})
const showChat = computed(() => {
  if (!loggedIn.value) return false
  // Chat is not gated on any onboarding state: payouts carry their own
  // downstream checks and chat has no link to either.
  if (/^(admin|creator|labels|scouts|superadmin)\./.test(host.value)) return true
  // Path fallback for local dev / single-domain setups without subdomains.
  // Allow an optional locale prefix (e.g. /de/admin/...). Note the labels
  // and scouts subdomains rewrite paths to look like creator/scouts paths
  // (no `/labels` prefix in route.path; scouts.* strips `/scouts`), so the
  // host check above is the gate for those — the path regex doesn't need
  // a labels or scouts arm. The `/scouts` apex fallback is included so
  // legacy invite-email URLs (which still land on the bare apex before
  // the redirect kicks in) get the chat panel during the short window
  // before the post-login bounce to scouts.*.
  return /^\/(?:[a-z]{2}\/)?(admin|creator|superadmin|scouts)(\/|$)/.test(route.path)
})

useHead({
  // Google zeigte „Diztree — Music Distribution & Audio Marketplace - Diztree":
  // die Startseite (und jede Seite, deren Titel den App-Namen schon traegt)
  // bekam den Namen ein zweites Mal angehaengt. Steht er drin, bleibt der
  // Titel, wie er ist.
  titleTemplate: (titleChunk) => {
    if (!titleChunk) return appName.value
    const name = String(appName.value || '').trim()
    if (name && titleChunk.toLowerCase().includes(name.toLowerCase())) return titleChunk
    return `${titleChunk} - ${name || 'Diztree'}`
  },
  htmlAttrs: {
    class: 'dark'
  }
})

// Apply tenant theme colors as inline CSS on :root during SSR so there's
// no flash of default colors before the client plugin runs.
const themeStyle = computed(() => {
  const colors = tenant.value?.theme?.colors
  if (!colors || !Object.keys(colors).length) return ''

  const map = {
    bgColor: '--bg-color', surfaceColor: '--surface-color', surfaceHover: '--surface-hover',
    primary: '--primary', primaryChannels: '--primary-channels', primaryGlow: '--primary-glow',
    secondary: '--secondary', secondaryChannels: '--secondary-channels', secondaryGlow: '--secondary-glow',
    accent: '--accent', textMain: '--text-main', textMuted: '--text-muted', textDim: '--text-dim',
    border: '--border', borderActive: '--border-active', glassBg: '--glass-bg', glassBorder: '--glass-border',
  }

  const vars = Object.entries(map)
    .filter(([key]) => colors[key])
    .map(([key, cssVar]) => `${cssVar}: ${colors[key]}`)
    .join('; ')

  return vars ? `:root { ${vars} }` : ''
})

useHead({
  style: [{ innerHTML: themeStyle }]
})
</script>
